/*global mzFunc */
import React, { useState, useEffect } from 'react'
import generalController from '../../../controllers/generalController'
import { languageController } from '../../../controllers/language/languageController'
import { convertLangCodeToSingleLetter, displayOutsideComponents, hideOutsideComponents, pushToGTMDataLayer, scrollToTop, handleAnalyticsTrackingCodes } from '../../../utils/helpers'

import FormStepsNav from '../../2_Molecules/FormStepsNav/FormStepsNav'
import StepWrapper from '../../2_Molecules/StepWrapper/StepWrapper'
import SelectVehicleStep from '../../4_Templates/SelectVehicleStep/SelectVehicleStep'

import SelectCurrentVehicleStep from './SelectCurrentVehicleStep/SelectCurrentVehicleStep'
import YourInformationStep from './YourInformationStep/YourInformationStep'
import FindDealerStep from './FindDealerStep/FindDealerStep'
import ValueEstimateStep from './ValueEstimateStep/ValueEstimateStep'

interface CurrentVehicleData {
  tradeInId: number | string
  tradeInMake: string
  tradeInModel: string
  tradeInYear: number
  tradeInTrim: string
  tradeInPriceLow: number
  tradeInPriceHigh: number
  searchTerm: string
  tradeInOdometer: number | string
}

interface UserInformation {
  firstname: string
  lastname: string
  email: string
  phone: string
  preferredContact: string
  message: string
  purchaseDate: string
}

interface VehicleData {
  id: number | string
  year: number | string
  model: string
  modelYear?: number
  msrp: number
  title?: string
  description?: string
  visual?: any
  isEV?: boolean
}

interface DealerData {
  dealer_code?: string | number
  dealer?: {
    name?: string
    [key: string]: any
  }
  userPosition?: {
    lat: number
    lng: number
  }
  [key: string]: any
}

interface TradeInEstimatorData {
  vehicleData: VehicleData | null
  dealerData: DealerData | null
  currentVehicleData: CurrentVehicleData | null
  userInformation: UserInformation | null
  isOptin?: boolean
}

interface FormExtraParams {
  source: string
  language: string
  lastStepBottomContents?: string
  initStepTopContents?: string
  buildAndPriceCtaLink?: string
  introductionText?: string
  formDisclaimer?: string
}

interface TradeInEstimatorProps {
  formExtraParams: FormExtraParams
  preDefinedVehicleData?: VehicleData | null
  preDefinedDealerData?: DealerData | null
  preDefinedUserLocation?: { lat: number; lng: number } | null
  containerRef: React.RefObject<HTMLDivElement>
}

declare global {
  interface Window {
    bnp_retailer_mode?: boolean
    mzFunc?: {
      checkCookie: (name: string) => string | null
    }
  }
}

const TradeInEstimator: React.FC<TradeInEstimatorProps> = ({
  formExtraParams,
  preDefinedVehicleData,
  preDefinedDealerData,
  preDefinedUserLocation,
  containerRef
}) => {
  const { source, language, lastStepBottomContents, initStepTopContents, buildAndPriceCtaLink, introductionText, formDisclaimer } = formExtraParams
  const maxStepNum = 5;
  const initStep = 1;

  const [currentStep, setCurrentStep] = useState<number>(initStep)
  const [currentProgress, setCurrentProgress] = useState<number>(initStep)

  const [data, setData] = useState<TradeInEstimatorData>({
    vehicleData: preDefinedVehicleData || null,
    dealerData: preDefinedDealerData || null,
    currentVehicleData: null,
    userInformation: null
  })

  // Display top contents on the init step.
  useEffect(() => {
    if (currentStep === initStep) {
      displayOutsideComponents(initStepTopContents)
    }
    // Add the form-related vars to DTM dataLayer.
    pushToGTMDataLayer({ form: 'Trade-in Estimator' });
    pushToGTMDataLayer({ formid: 'TiE' });
  }, [])

useEffect(() => {
    const heroContainer = document.querySelector('.form-hero-container');
    const heroEl = document.getElementById('mz-hero');
    if (!heroContainer || !heroEl || heroEl.parentElement === heroContainer) return;

    const originalParentEl = heroEl.parentElement;
    heroEl.classList.remove('visually-hidden');
    heroContainer.appendChild(heroEl);

    // Restore the hero block when the form is unmounted.
    return () => {
      if (originalParentEl) originalParentEl.appendChild(heroEl);
      heroEl.classList.add('visually-hidden');
    };
  }, [])

  useEffect(() => {
    if(typeof mzFunc === 'object') {
      console.log(mzFunc);
      mzFunc.updateOffsets();
    }

    // Update 'formstep' dataLayer var when currentStep is changed
    pushToGTMDataLayer({ formstep: `Step${currentStep}` });
  }, [currentStep])

  const handleNextStep = (newProps: Partial<TradeInEstimatorData>) => {
    if (currentStep === maxStepNum) return;

    let nextStep: number;
    if (window.bnp_retailer_mode) {
      nextStep = maxStepNum;
    } else {
      nextStep = currentStep + 1;
    }


    // Manage visibility of components outside of the react app.
    if (nextStep === initStep + 1) {
      // Hide TiE Hero and Intro section
      hideOutsideComponents(initStepTopContents)
    } else if (nextStep === maxStepNum) {
      // Display TiE related bottom contents on confirmation step.
      displayOutsideComponents(lastStepBottomContents)
    }

    setData({ ...data, ...newProps })
    setupTrackingCodes({ ...data, ...newProps }, currentStep)

    // If vehicle data is already defined in URL params,
    // skip step 2.
    if (nextStep === 2 && preDefinedVehicleData && currentProgress < 2) {
      setCurrentStep(3);
      setCurrentProgress(3);
      setupTrackingCodes(data, 2);
    } else {
      setCurrentStep(nextStep)
      setCurrentProgress(prevState => nextStep > prevState ? nextStep : prevState)
    }

    // Scroll to the top of the screen.
    scrollToTop();
  }

  // Go back to the init step
  const refreshSteps = () => {
    setCurrentStep(initStep)
    displayOutsideComponents(initStepTopContents)
    hideOutsideComponents(lastStepBottomContents)
    scrollToTop()
  }

  // Handle adding analytics tracking attributes.
  const setupTrackingCodes = async (data: Partial<TradeInEstimatorData>, completedStep: number) => {
    try {
      // Set vehicle-related tracking attributes
      if (completedStep === 2 && data.vehicleData) {
        const trackData = { modelCarlineName: data.vehicleData.model, modelYear: data.vehicleData.modelYear, msrp: data.vehicleData.msrp };
        handleAnalyticsTrackingCodes(trackData, 'Vehicle Select');
      }
      // Set user-related tracking attributes
      if (completedStep === 3 && data.userInformation) {
        const trackData = { email: data.userInformation.email, firstname: data.userInformation.firstname, lastname: data.userInformation.lastname };
        handleAnalyticsTrackingCodes(trackData, 'User Information');
      }
      // Set dealer-related tracking attributes
      if (completedStep === 4 && data.dealerData) {
        let trackData: any = { dealerName: data.dealerData.dealer?.name };
        handleAnalyticsTrackingCodes(trackData, 'Dealer Select');

        trackData = { optin: data.isOptin === true ? 'optin' : 'optout' };
        handleAnalyticsTrackingCodes(trackData, 'User Consent');
      }
    } catch (err) {
      console.log(err)
    }
  }

  return (
    <>
      {!window.bnp_retailer_mode &&
        <FormStepsNav
          isActive={(currentStep < maxStepNum)}
          currentStep={currentStep}
          currentProgress={currentProgress}
          setCurrentStep={(nextStep: number) => {
            // If selected step is init step, show init top contents.
            if (nextStep === initStep) {
              displayOutsideComponents(initStepTopContents)
            }
            // If selected step is greater than init step, hide init top contents.
            else if (nextStep > initStep) {
              hideOutsideComponents(initStepTopContents)
            }
            setCurrentStep(nextStep)
          }}
          navList={[
            {
              step: 1,
              label: languageController.getCopy('tradeInEstimator', 'step1') || '',
            },
            {
              step: 2,
              label: languageController.getCopy('tradeInEstimator', 'step2') || '',
            },
            {
              step: 3,
              label: languageController.getCopy('tradeInEstimator', 'step3') || '',
            },
            {
              step: 4,
              label: languageController.getCopy('tradeInEstimator', 'step4') || '',
            },
          ]}
        />
      }
      <div
        className="mz-trade-in-estimator"
        data-current-step={currentStep}
      >
        <div className="mz-trade-in-estimator__steps">
          {/* Step 1: Select current vehicle */}
          <StepWrapper
            className={`mz-trade-in-estimator__select-current-vehicle`}
            stepNum={1}
            currentStep={currentStep}
            currentProgress={currentProgress}
          >
            <div className="form-hero-container" />
            <SelectCurrentVehicleStep handleNextStep={handleNextStep} topText={introductionText} />
          </StepWrapper>
          {/* Step 2: Select a desired vehicle */}
          <StepWrapper
            className={`mz-trade-in-estimator__select-desired-vehicle`}
            stepNum={2}
            currentStep={currentStep}
            currentProgress={currentProgress}
          >
            <SelectVehicleStep
              handleNextStep={(data) => handleNextStep(data as Partial<TradeInEstimatorData>)}
              title={languageController.getCopy('tradeInEstimator', 'step2Title')}
              preDefinedModelId={data.vehicleData?.id ? String(data.vehicleData.id) : undefined}
            />
          </StepWrapper>
          {/* Step 3: Enter user's information */}
          <StepWrapper
            className={`mz-trade-in-estimator__user-information`}
            stepNum={3}
            currentStep={currentStep}
            currentProgress={currentProgress}
          >
            <YourInformationStep
              language={language}
              handleNextStep={handleNextStep}
            />
          </StepWrapper>
          {/* Step 4: Select a dealer */}
          {!window.bnp_retailer_mode &&
            <StepWrapper
              className={`mz-trade-in-estimator__select-dealer`}
              stepNum={4}
              currentStep={currentStep}
              currentProgress={currentProgress}
            >
              <FindDealerStep
                handleNextStep={handleNextStep}
                currentVehicleData={data.currentVehicleData}
                desiredVehicleData={data.vehicleData}
                userInformation={data.userInformation}
                source={source}
                language={convertLangCodeToSingleLetter(language as 'en' | 'fr')}
                formDisclaimer={formDisclaimer || ''}
                preDefinedUserLocation={preDefinedUserLocation}
                preDefinedDealerData={data.dealerData?.dealer}
              />
            </StepWrapper>
          }
          {/* Step 5: Show value estimate */}
          <StepWrapper
            className={`mz-trade-in-estimator__value-estimate`}
            stepNum={5}
            currentStep={currentStep}
            currentProgress={currentProgress}
          >
            <ValueEstimateStep
              currentVehicleData={data.currentVehicleData}
              desiredVehicleData={data.vehicleData}
              refreshSteps={refreshSteps}
              language={language}
              buildAndPriceCtaLink={buildAndPriceCtaLink || ''}
            />
          </StepWrapper>
        </div>
      </div>
    </>
  )
}

export default TradeInEstimator
